import React, { useState } from 'react';
import { Calendar, Rocket, Target, Users, Zap, Code, BookOpen, ChevronRight } from 'lucide-react';

const RoadmapView = () => {
  const [selectedPhase, setSelectedPhase] = useState(0);

  const initiatives = [
    {
      phase: "Story Engine Platform",
      timeline: "Q1 2025 (Weeks 1-3)",
      description: "Transform our reader into a modular story engine that other projects can leverage",
      status: "In Progress",
      icon: <BookOpen className="w-5 h-5" />,
      tasks: [
        "Modular chapter system with dynamic branching",
        "Token-gated narrative paths",
        "Creator dashboard for story management",
        "Documentation for third-party integration"
      ],
      impact: "Foundation for ecosystem growth",
      completion: 35
    },
    {
      phase: "Social Amplification",
      timeline: "Q1 2025 (Weeks 4-7)",
      description: "Implement tweet-to-earn mechanics integrated with token holdings",
      status: "Planned",
      icon: <Users className="w-5 h-5" />,
      tasks: [
        "Holder verification system",
        "Tweet tracking and analytics",
        "Automated reward distribution",
        "Community leaderboards"
      ],
      impact: "Drive viral growth and engagement",
      completion: 0
    },
    {
      phase: "Cross-Project Bridge",
      timeline: "Q1 2025 (Weeks 8-12)",
      description: "Create shared universe between WarLord and Spooky",
      status: "Planned",
      icon: <Target className="w-5 h-5" />,
      tasks: [
        "Shared narrative framework",
        "Cross-project token utility",
        "Joint community events system",
        "Combined holder benefits"
      ],
      impact: "Unified ecosystem value",
      completion: 0
    }
  ];

  return (
    <div style={{
      width: '100%',
      maxWidth: '1200px',
      height: '700px',
      backgroundColor: '#f5f5f5',
      borderRadius: '8px',
      display: 'flex',
      overflow: 'hidden',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      position: 'relative'
    }}>
      {/* Left Page - Navigation */}
      <div style={{
        flex: '0 0 300px',
        borderRight: '1px solid #1a1a1a',
        position: 'relative',
        backgroundColor: '#111',
        backgroundImage: 'linear-gradient(to right, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0) 10%)'
      }}>
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-800 bg-black/30">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <h2 className="text-lg font-mono text-green-500">
                SYSTEM STATUS
              </h2>
            </div>
            <div className="font-mono text-xs text-gray-500 space-y-1">
              <p>BUILD: v0.1.3</p>
              <p className="flex items-center gap-2">
                STATUS: <span className="text-green-500 animate-pulse">[ACTIVE]</span>
              </p>
            </div>
          </div>

          {/* Phase Navigation */}
          <div className="flex-1 overflow-y-auto">
            {initiatives.map((phase, index) => (
              <button
                key={index}
                onClick={() => setSelectedPhase(index)}
                className={`w-full border-b border-gray-800 transition-all relative
                  ${selectedPhase === index ? 'bg-purple-500/10' : 'hover:bg-gray-800/30'}`}
              >
                {/* Active Indicator */}
                {selectedPhase === index && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-purple-500 animate-pulse" />
                )}

                <div className="p-4 flex items-center gap-3">
                  <div className={`p-2 rounded ${
                    selectedPhase === index ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-800 text-gray-500'
                  }`}>
                    {phase.icon}
                  </div>

                  <div className="flex-1 text-left">
                    <div className={`font-mono text-sm mb-1 ${
                      selectedPhase === index ? 'text-purple-400' : 'text-gray-400'
                    }`}>
                      PHASE_{index + 1}
                    </div>
                    <div className={`text-sm ${
                      selectedPhase === index ? 'text-gray-200' : 'text-gray-500'
                    }`}>
                      {phase.phase}
                    </div>
                  </div>

                  <ChevronRight className={`w-5 h-5 ${
                    selectedPhase === index ? 'text-purple-400' : 'text-gray-700'
                  }`} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right Page - Details */}
      <div style={{
        flex: 1,
        backgroundColor: '#111',
        backgroundImage: 'linear-gradient(to left, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0) 10%)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Details Header */}
        <div className="p-6 border-b border-gray-800 bg-black/30">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500 font-mono mb-1">
                PHASE_{selectedPhase + 1}
              </div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                {initiatives[selectedPhase].phase}
              </h3>
            </div>
            <div className="flex flex-col items-end">
              <div className={`px-3 py-1 rounded-full text-xs font-mono ${
                initiatives[selectedPhase].status === "In Progress"
                  ? "bg-green-500/20 text-green-400 border border-green-500/30 animate-pulse"
                  : "bg-gray-800 text-gray-500 border border-gray-700"
              }`}>
                {initiatives[selectedPhase].status}
              </div>
              <div className="mt-2 w-32 h-1 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
                  style={{ width: `${initiatives[selectedPhase].completion}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Details Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-6">
            {/* Mission Objectives */}
            <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-800">
              <div className="flex items-center gap-2 text-sm text-purple-400 mb-2 font-mono">
                <Rocket className="w-4 h-4" />
                <span>MISSION_OBJECTIVES</span>
              </div>
              <p className="text-gray-300 leading-relaxed">
                {initiatives[selectedPhase].description}
              </p>
            </div>

            {/* Key Deliverables */}
            <div>
              <div className="flex items-center gap-2 text-sm text-purple-400 mb-3 font-mono">
                <Code className="w-4 h-4" />
                <span>KEY_DELIVERABLES</span>
              </div>
              <div className="grid gap-2">
                {initiatives[selectedPhase].tasks.map((task, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 rounded bg-gray-900/50 border border-gray-800 hover:bg-gray-900/70 transition-all group"
                  >
                    <div className="w-6 h-6 rounded-full border-2 border-purple-500/30 flex items-center justify-center text-xs text-purple-400 flex-shrink-0 mt-0.5 group-hover:border-purple-500 transition-colors">
                      {index + 1}
                    </div>
                    <span className="text-gray-300 font-mono text-sm">{task}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Impact Assessment */}
            <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-800">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-yellow-500" />
                <div>
                  <span className="text-yellow-500 font-mono">IMPACT_ASSESSMENT: </span>
                  <span className="text-gray-300">{initiatives[selectedPhase].impact}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoadmapView;
